import _, {findLast, findLastIndex, first, isNull, isUndefined, last} from 'lodash';
import {Key, useEffect, useState} from 'react';

import {usePrevious, useTimeout} from '../../../../../libs/shared/core/src/helpers/react/hooks/hookHelpers';
import {AsyncMoreStatusesEnum} from '../../../../../libs/shared/core/src/helpers/types/asyncStatuses';
import {isDefined} from '../../../../../libs/shared/core/src/helpers/typescript/typeHelpers';
import {
  DraftMessageModel,
  TimelineMessageModel,
} from '../../../../../libs/shared/core/src/models/api/messageModel';
import {
  isActivityInConversationRelevant,
  isActivityInConversationRuleNotification,
  isActivityInConversationUnreadMention,
  isConversationInTeamContext,
} from '../../../../../src/helpers/models/conversationHelpers';
import {ActivityModel, ActivityVisualTypesEnum} from '../../../../../src/models/api/activityModel';
import {AnnotationModel} from '../../../../../src/models/api/annotationModel';
import {ConversationModel} from '../../../../../src/models/api/conversationModel';
import {computeIsAutoResponseMessageActivitySource} from '../../../../../src/store/data/activities/autoResponseMessageActivityHelpers';
import {
  findParametersActivityDate,
  findParametersActivityId,
} from '../../../../../src/store/panels/inboxes/parameters/inboxesParametersHelpers';
import {SendingActivity} from '../../../../../src/store/panels/viewer/viewerPanelSelectors';
import {ViewerPanelConversationPageState} from '../../../../../src/store/panels/viewer/viewerPanelState';
import {COMMENT_JUMP_ANIMATION_DURATION_MS, PINNED_COMMENT_ANIMATION_DURATION_MS} from './animationHelpers';

/*
 * Constants.
 */

export const MILESTONE_ACTIVITY = 'milestone-newactivity';

/*
 * Helpers.
 */

export interface LastTimelineMessage {
  activityId?: number;
  orphanSentMessageUid?: string;
  sendingActivityIndex?: number;
}

/** @returns the last message that is displayed in the timeline. */
export function findLastTimelineManualMessage(
  activities: ReadonlyArray<ActivityModel>,
  orphanSentMessages: ReadonlyArray<DraftMessageModel | TimelineMessageModel>,
  sendingActivities: ReadonlyArray<SendingActivity>,
  hasComposeDrafts: boolean,
) {
  if (hasComposeDrafts) {
    // A draft is the last message.
    return {};
  }

  const lastSendingMessageActivityIndex = findLastIndex(sendingActivities, (a) => isDefined(a.message));
  if (lastSendingMessageActivityIndex !== -1) {
    // We have a sending message, which is the last in the timeline.
    return {
      sendingActivityIndex: lastSendingMessageActivityIndex,
    };
  }

  const lastOrphanSentMessage = last(orphanSentMessages);
  if (lastOrphanSentMessage) {
    // We have a sent message without an activity, which is the last in the timeline.
    return {
      orphanSentMessageUid: lastOrphanSentMessage.uid,
    };
  }

  // Last message that was manually sent.
  const lastMessageActivity = findLast(
    activities,
    (a) =>
      a.visualType === ActivityVisualTypesEnum.MESSAGE &&
      !computeIsAutoResponseMessageActivitySource(a.source?.visualType),
  );
  if (lastMessageActivity) {
    // We have a message activity, which is the last in the timeline.
    return {
      activityId: lastMessageActivity.id,
    };
  }

  // There is no last message.
  return {};
}

/** Returns the initial activity to focus. */
interface FindFocusKeyProps {
  page: ViewerPanelConversationPageState;
  activities: ReadonlyArray<ActivityModel>;
  conversation: ConversationModel;
  userAlias: string;
  composeDraftMessages: ReadonlyArray<DraftMessageModel>;
  hasTeamAutoscrollP3ToNewActivity: boolean;
}

// TODO: Dedupe with computeRefetchConversationAroundDate.
export function findFocusKey(props: FindFocusKeyProps): number | string | undefined {
  const {page, activities, conversation, userAlias, composeDraftMessages, hasTeamAutoscrollP3ToNewActivity} =
    props;
  const {cellsContext, originalConversationState, parameters} = page;

  if (parameters.focusDraftUid !== undefined) {
    return parameters.focusDraftUid;
  }

  const aroundActivityId = findParametersActivityId(parameters);
  const aroundActivityDate = findParametersActivityDate(parameters);

  // If the top was requested, focus the first activity if we have it.
  if (aroundActivityDate === 0) {
    return page.moreStatusTop === AsyncMoreStatusesEnum.ALL_LOADED ? first(activities)?.id : undefined;
  }

  // If a specific activity was requested, focus it.
  if (!isUndefined(aroundActivityId)) {
    // We may have negative id in case we matched a draft.
    if (aroundActivityId < 0) {
      const draftToFocus = composeDraftMessages.find((draft) => draft.id === Math.abs(aroundActivityId));
      return draftToFocus?.uid;
    }

    return activities.find((a) => a.id === aroundActivityId)?.id;
  }

  const lastFocusKey = findLastFocusKey(props);

  // If the bottom was requested, focus the last activity.
  if (aroundActivityDate === Infinity) {
    return lastFocusKey;
  }

  // If a specific date was requested, focus the nearest activity.
  if (!isUndefined(aroundActivityDate)) {
    return _(activities)
      .filter(isActivityInConversationRelevant)
      .minBy((a) => Math.abs(aroundActivityDate - a.date))?.id;
  }

  const isTeamContext = isConversationInTeamContext(cellsContext, conversation);

  // In a team context, always start at the end (without FF).
  if (isTeamContext && !hasTeamAutoscrollP3ToNewActivity) {
    return lastFocusKey;
  }

  if (!originalConversationState) {
    return lastFocusKey;
  }

  const {seenAt, autoSeenAt} = originalConversationState;

  // If you've been at-mentioned, scroll to first unread mention, should not scroll if you have marked a conversation as unread.
  const unreadUserMention = activities.find((a) =>
    isActivityInConversationUnreadMention(a, userAlias, autoSeenAt),
  );

  if (unreadUserMention) {
    return unreadUserMention.id;
  }

  // If you've never seen the conversation, start at the beginning (personal context) or end (team context).
  if (!seenAt) {
    return isTeamContext ? lastFocusKey : first(activities)?.id;
  }

  // If there are unread messages, focus the new activity line.
  const firstUnreadActivity = activities.find((a) => isActivityInConversationRelevant(a) && a.date > seenAt);
  if (firstUnreadActivity) {
    // The new activity only exists if there are both read and unread activities (of any type).
    return activities.some((activity) => activity.date <= seenAt)
      ? MILESTONE_ACTIVITY
      : firstUnreadActivity.id;
  }

  // If unread rule notification, focus there to show why you're notified on the conversation
  // TODO: Should this live with the special exception for mentions above? Would match hasUnreadBeforeFetchedContent.
  const unreadRuleNotification = activities.find((a) =>
    isActivityInConversationRuleNotification(a, autoSeenAt),
  );
  if (unreadRuleNotification) {
    return unreadRuleNotification.id;
  }

  // Finally, if no other focus activity, focus the last relevant activity.
  return lastFocusKey;
}

export function isActivityIdFocusKey(focusKey: Key | undefined): focusKey is number {
  // The findFocusKey function above returns a number only when it's an activity.
  // TODO: Make this less hacky.
  return typeof focusKey === 'number';
}

/** Finds the last focusable ("relevant") activity. */
export function findLastActivityId(props: FindFocusKeyProps): number | undefined {
  return findLast(props.activities, isActivityInConversationRelevant)?.id;
}

/** Finds the last focusable timeline item. */
export function findLastFocusKey(props: FindFocusKeyProps): string | number | undefined {
  // If there's a draft, that's the last focusable item.
  const lastDraftMessageUid = last(props.composeDraftMessages)?.uid;
  return lastDraftMessageUid ?? findLastActivityId(props);
}

export function maybeUpdateFirstVisibleActivityDate(
  page: ViewerPanelConversationPageState,
  firstVisibleActivityDate: number,
  setFirstVisibleActivityDate: (arg: number) => void,
  setIsAtTopOfTimeline: (arg: boolean) => void,
  firstActivity?: ActivityModel,
  firstVisibleActivity?: ActivityModel,
) {
  if (!firstVisibleActivity || !page.originalConversationState) {
    return;
  }

  const originalSeenAt = page.originalConversationState.seenAt;

  // If our first visible activity is earlier than the previous visible, update earliest visible date
  if (
    firstVisibleActivity.date < firstVisibleActivityDate &&
    (!originalSeenAt || firstVisibleActivity.date > originalSeenAt)
  ) {
    setFirstVisibleActivityDate(firstVisibleActivity.date);
  }

  // Otherwise, if we've seen this conversation before and we're viewing already viewed activities,
  // set our originalSeenAt date as the earliest viewed activity
  if (originalSeenAt && firstVisibleActivity.date < originalSeenAt) {
    setFirstVisibleActivityDate(originalSeenAt);
  }

  // If we're looking at the top of the timeline, we have no need to load the unread banner
  if (firstActivity && firstVisibleActivity.date <= firstActivity.date) {
    setIsAtTopOfTimeline(true);
  } else {
    setIsAtTopOfTimeline(false);
  }
}

export function maybeUpdateLastVisibleActivityDate(
  lastVisibleActivityDate: number,
  lastVisibleActivityId: Key,
  isAtBottomOfTimeline: boolean,
  setIsAtBottomOfTimeline: (arg: boolean) => void,
  setLastVisibleActivityDate: (arg: number) => void,
  firstVisibleActivity?: ActivityModel,
  lastVisibleActivity?: ActivityModel,
) {
  // If the activity is not in our activities list it's generally a draft/recently sent
  if (!lastVisibleActivity && lastVisibleActivityId !== MILESTONE_ACTIVITY) {
    setIsAtBottomOfTimeline(true);
  }

  // If we're at the bottom of the timeline but we can see activities above it, we can still see
  // new incoming messages
  if (isAtBottomOfTimeline && firstVisibleActivity) {
    setLastVisibleActivityDate(Date.now());
  }

  if (!lastVisibleActivity) {
    return;
  }

  setIsAtBottomOfTimeline(false);
  if (lastVisibleActivity.date > lastVisibleActivityDate) {
    setLastVisibleActivityDate(lastVisibleActivity.date);
  }
}

/*
 * Hook helping with the comment highlight states.
 */
interface HighlightCommentProps {
  jumpKey?: Key;
  isHighlighted?: boolean;
  jumpSourceActivityId?: number;
  annotations: ReadonlyArray<AnnotationModel>;
}
export function useIsCommentVisuallyHighlighted(props: HighlightCommentProps) {
  // We want to flash the comment background color when it gets jumped to (e.g. from a referenced or pinned comment)
  const wasJustHighlightedAnimationDurationMs = COMMENT_JUMP_ANIMATION_DURATION_MS;
  const shouldSetWasJustHighlighted = useShouldHighlightComment(props);
  const wasJustHighlighted = useFlagWithTimeout(
    shouldSetWasJustHighlighted,
    wasJustHighlightedAnimationDurationMs,
  );
  return wasJustHighlighted;
}

export function useIsCommentVisuallyPinned(isPinned: boolean) {
  // We want to animate the comment background color when it gets pinned for a few seconds,
  // so we set `wasJustPinned` to true during that time.
  const wasJustPinnedAnimationDurationMs = PINNED_COMMENT_ANIMATION_DURATION_MS;
  const previousIsPinned = usePrevious(isPinned);
  const wasJustPinned = useFlagWithTimeout(
    !isNull(previousIsPinned) && Boolean(!previousIsPinned && isPinned),
    wasJustPinnedAnimationDurationMs,
  );
  return wasJustPinned;
}

function useShouldHighlightComment(props: HighlightCommentProps) {
  const {jumpKey, isHighlighted, jumpSourceActivityId, annotations} = props;
  const previousJumpKey = usePrevious(jumpKey);
  const previousIsHighlighted = usePrevious(isHighlighted);

  const isQuoteCommentJump =
    jumpSourceActivityId !== undefined &&
    annotations.length > 0 &&
    annotations.some((annotation) => annotation.commentActivityId === jumpSourceActivityId);

  // Trigger a highlight animation when the flag transitions from falsy to truthy.
  if (!previousIsHighlighted && isHighlighted && !isQuoteCommentJump) {
    return true;
  }

  // Re-trigger a highlight animation when the flag remains truthy, and the jump key changes.
  if (isHighlighted && jumpKey !== previousJumpKey && !isQuoteCommentJump) {
    return true;
  }

  return false;
}

export function useFlagWithTimeout(shouldSetFlag: boolean, timeoutMs: number) {
  const [safeSetTimeout] = useTimeout();
  const [flag, setFlag] = useState(false);

  useEffect(() => {
    if (shouldSetFlag) {
      setFlag(true);
      safeSetTimeout(() => {
        setFlag(false);
      }, timeoutMs);
    }
  }, [safeSetTimeout, shouldSetFlag, timeoutMs]);

  return flag;
}
